import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { PokemonCard } from '../../types/pokemon';
import CardDetailView from '../../components/CardDetailView/CardDetailView';
import { usePokemonSetCards } from '../../hooks/useMasterSets';
import type { CardOwnershipData } from '../../components/CardOwnershipForm/CardOwnershipForm';
import {
  useCardOwnership,
  useUpdateCardOwnership,
  useToggleCardOwnership,
} from '../../hooks/useCardOwnership';
import {
  useToggleWishlist,
  useToggleFavorite,
} from '../../hooks/useUserIntents';

const PokemonCardDetails: React.FC = () => {
  const { setId, cardId } = useParams<{ setId: string; cardId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromSearch = location.state?.fromSearch;
  const searchUrl = location.state?.searchUrl;

  // Load cards for this set using TanStack Query
  const { data: cardsArray = [], isLoading: loading } = usePokemonSetCards(setId);

  // Find the specific card
  const card = useMemo(() => {
    if (!cardsArray.length || !cardId) return null;
    
    // Extract base card ID without suffix if present
    const baseCardId = cardId.split('_')[0];

    return (cardsArray as any[]).find(
      (c: any) =>
        c.id === cardId ||
        c.id === baseCardId ||
        c.id?.startsWith(baseCardId),
    ) as PokemonCard || null;
  }, [cardsArray, cardId]);

  const [showSnackbar, setShowSnackbar] = useState(false);

  // Use card ownership hooks
  const { data: ownership } = useCardOwnership(cardId || '');
  const upsertOwnership = useUpdateCardOwnership();
  const toggleOwnershipMutation = useToggleCardOwnership();
  const toggleWishlistMutation = useToggleWishlist();
  const toggleFavoriteMutation = useToggleFavorite();

  const handleSave = async (data: CardOwnershipData) => {
    if (!cardId || !setId) return;

    try {
      await upsertOwnership.mutateAsync({
        cardId,
        data: {
          setId,
          ...data,
        },
      });
      setShowSnackbar(true);
      setTimeout(() => {
        navigate(`/sets/${setId}`);
      }, 1500);
    } catch (error) {
      console.error('Failed to save card ownership:', error);
    }
  };

  const handleToggle = () => {
    // Only call mutation if we are REMOVING ownership (currently owned)
    if (cardId && setId && ownership?.owned) {
      toggleOwnershipMutation.mutate(cardId);
    }
  };

  const breadcrumbs = fromSearch ? [
    { label: 'Search Results', onClick: () => navigate(searchUrl || '/search') },
    { label: card?.name || cardId || '', onClick: () => {} },
  ] : [
    { label: 'Pokemon Sets', onClick: () => navigate('/sets') },
    { label: card?.set?.name || setId || '', onClick: () => navigate(`/sets/${setId}`) },
  ];

  return (
    <CardDetailView
      card={card}
      loading={loading}
      gameType="pokemon"
      setId={setId || ''}
      cardId={cardId || ''}
      onBack={() => {
        if (fromSearch && searchUrl) {
          navigate(searchUrl);
        } else {
          navigate(`/sets/${setId}`);
        }
      }}
      breadcrumbs={breadcrumbs}
      ownership={ownership}
      onToggleOwnership={handleToggle}
      onToggleWishlist={() => cardId && card && setId && toggleWishlistMutation.mutate({
        game: 'pokemon',
        setId,
        cardId,
        isWishlist: !ownership?.isWishlist,
        cardName: card.name,
        imageUrl: card.images.small,
        rarity: card.rarity,
        types: (card as any).types,
        cardNumber: card.number,
        baseCardId: card.id
      })}
      onToggleFavorite={() => cardId && card && setId && toggleFavoriteMutation.mutate({
        game: 'pokemon',
        setId,
        cardId,
        isFavorite: !ownership?.isFavorite,
        cardName: card.name,
        imageUrl: card.images.small,
        rarity: card.rarity,
        types: (card as any).types,
        cardNumber: card.number,
        baseCardId: card.id
      })}
      onSaveOwnership={handleSave}
      isSaving={upsertOwnership.isLoading}
      isToggling={toggleWishlistMutation.isPending || toggleFavoriteMutation.isPending}
      showSnackbar={showSnackbar}
      onCloseSnackbar={() => setShowSnackbar(false)}
      pokemonExtras={{
        tcgplayerUrl: card?.tcgplayer?.url,
        setReleaseDate: card?.set?.releaseDate,
      }}
    />
  );
};

export default PokemonCardDetails;
